import { motion } from "framer-motion";
import { Video, PoundSterling, Scissors } from "lucide-react";
import SceneShell from "../components/SceneShell";
import CountUp from "../components/CountUp";

/**
 * Scene 23 — Set the baseline (current model).
 * Three stat cards: 20,000 videos · £45 revenue · £5 edit cost.
 */

const stats = [
  {
    icon: Video,
    label: "Videos per year",
    value: 20000,
    prefix: "",
    decimals: 0,
    accent: "#00deb6",
    note: "Current annual volume",
  },
  {
    icon: PoundSterling,
    label: "Revenue per video",
    value: 45,
    prefix: "£",
    decimals: 0,
    accent: "#96C11F",
    note: "Average selling price",
  },
  {
    icon: Scissors,
    label: "Edit cost per video",
    value: 5,
    prefix: "£",
    decimals: 0,
    accent: "#c83a4f",
    note: "Extremely competitive",
  },
];

export default function Scene23_BaselineCurrent() {
  return (
    <SceneShell variant="forest" align="left" data-testid="scene-baseline-current">
      <div className="relative max-w-[1500px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mb-6"
        >
          Video commercials · set the baseline
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-5xl sm:text-6xl lg:text-7xl text-white"
          data-testid="baseline-title"
        >
          Where we are{" "}
          <span style={{ color: "#96C11F" }}>today.</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="mt-6 max-w-2xl text-base sm:text-lg italic"
          style={{ color: "rgba(255,255,255,0.65)" }}
        >
          "Let's look at where we are today…"
        </motion.p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 24, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{
                  duration: 0.9,
                  delay: 1.0 + i * 0.18,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="rounded-2xl glass p-7 relative overflow-hidden"
                data-testid={`baseline-stat-${i}`}
              >
                <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
                  <div
                    className="absolute -bottom-10 -right-10 w-40 h-40 rounded-full opacity-30"
                    style={{
                      background: `radial-gradient(circle, ${s.accent} 0%, transparent 70%)`,
                      filter: "blur(20px)",
                    }}
                  />
                </div>
                <div className="relative">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
                    style={{
                      background: `${s.accent}20`,
                      color: s.accent,
                      border: `1px solid ${s.accent}55`,
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div
                    className="text-[11px] tracking-[0.32em] uppercase mb-3"
                    style={{ color: "rgba(255,255,255,0.55)" }}
                  >
                    {s.label}
                  </div>
                  <div
                    className="kn-display tnum text-[44px] sm:text-[54px] lg:text-[64px] leading-none whitespace-nowrap"
                    style={{ color: s.accent, fontWeight: 800 }}
                  >
                    <CountUp
                      from={0}
                      to={s.value}
                      prefix={s.prefix}
                      decimals={s.decimals}
                      duration={2.0}
                      startDelay={1.4 + i * 0.18}
                    />
                  </div>
                  <div
                    className="mt-3 text-sm"
                    style={{ color: "rgba(255,255,255,0.55)" }}
                  >
                    {s.note}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </SceneShell>
  );
}
